import React, { useState, useEffect } from 'react';
import { Bell, Mail, Smartphone, Clock, CheckCircle, AlertCircle, Save } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import authService from '../services/authService';
import { webPushService } from '../services/webPushService';

interface ReminderPrefs {
  email_24h_before: boolean;
  email_1h_before: boolean;
  push_1h_before: boolean;
  push_15min_before: boolean;
  confirmation_48h_before: boolean;
  post_meeting_feedback: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  push_enabled: boolean;
}

const ReminderPreferences: React.FC = () => {
  const [preferences, setPreferences] = useState<ReminderPrefs>({
    email_24h_before: true,
    email_1h_before: true,
    push_1h_before: true,
    push_15min_before: true,
    confirmation_48h_before: false,
    post_meeting_feedback: false,
    quiet_hours_start: null,
    quiet_hours_end: null,
    push_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const currentUser = authService.getCurrentSession()?.user;

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    if (!currentUser?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reminder_preferences')
        .select('*')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setPreferences({
          email_24h_before: data.email_24h_before,
          email_1h_before: data.email_1h_before,
          push_1h_before: data.push_1h_before,
          push_15min_before: data.push_15min_before,
          confirmation_48h_before: data.confirmation_48h_before,
          post_meeting_feedback: data.post_meeting_feedback,
          quiet_hours_start: data.quiet_hours_start,
          quiet_hours_end: data.quiet_hours_end,
          push_enabled: data.push_enabled,
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      showMessage('error', 'Error al cargar preferencias');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser?.id) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('reminder_preferences')
        .upsert({
          user_id: currentUser.id,
          ...preferences,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;

      showMessage('success', 'Preferencias guardadas correctamente');
    } catch (error) {
      console.error('Error saving preferences:', error);
      showMessage('error', 'Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleEnablePushNotifications = async () => {
    if (!webPushService.isSupported()) {
      showMessage('error', 'Tu navegador no soporta notificaciones push');
      return;
    }

    try {
      const success = await webPushService.subscribe();

      if (success) {
        setPreferences(prev => ({ ...prev, push_enabled: true }));
        showMessage('success', 'Notificaciones push habilitadas correctamente');

        // Recargar preferencias para obtener el token guardado
        await loadPreferences();
      } else {
        showMessage('error', 'Permiso denegado para notificaciones');
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      showMessage('error', 'Error al habilitar notificaciones push: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3">Cargando preferencias...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Bell className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Preferencias de Recordatorios</h2>
            <p className="text-sm text-gray-600">Configura cómo y cuándo recibir recordatorios de reuniones</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Success/Error Message */}
        {message && (
          <div className={`flex items-center space-x-2 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{message.text}</span>
          </div>
        )}

        {/* Email Notifications */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Mail className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Notificaciones por Email</h3>
          </div>

          <div className="space-y-3 ml-7">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={preferences.email_24h_before}
                onChange={(e) => setPreferences(prev => ({ ...prev, email_24h_before: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Email 24 horas antes de la reunión</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={preferences.email_1h_before}
                onChange={(e) => setPreferences(prev => ({ ...prev, email_1h_before: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Email 1 hora antes de la reunión</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={preferences.confirmation_48h_before}
                onChange={(e) => setPreferences(prev => ({ ...prev, confirmation_48h_before: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Solicitar confirmación 48 horas antes</span>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={preferences.post_meeting_feedback}
                onChange={(e) => setPreferences(prev => ({ ...prev, post_meeting_feedback: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">Solicitar feedback después de la reunión</span>
            </label>
          </div>
        </div>

        {/* Push Notifications */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Smartphone className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-medium text-gray-900">Notificaciones Push</h3>
            </div>
            {!preferences.push_enabled && (
              <button
                onClick={handleEnablePushNotifications}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
              >
                Habilitar notificaciones
              </button>
            )}
          </div>

          {preferences.push_enabled ? (
            <div className="space-y-3 ml-7">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={preferences.push_1h_before}
                  onChange={(e) => setPreferences(prev => ({ ...prev, push_1h_before: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Notificación 1 hora antes de la reunión</span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={preferences.push_15min_before}
                  onChange={(e) => setPreferences(prev => ({ ...prev, push_15min_before: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Notificación 15 minutos antes de la reunión</span>
              </label>
            </div>
          ) : (
            <p className="text-sm text-gray-500 ml-7 bg-gray-50 p-3 rounded-lg border border-gray-200">
              Habilita las notificaciones push para recibir alertas en tiempo real en tu navegador
            </p>
          )}
        </div>

        {/* Quiet Hours */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-medium text-gray-900">Horario de "No Molestar"</h3>
          </div>

          <div className="ml-7 space-y-3">
            <p className="text-sm text-gray-600 mb-3">
              No recibirás notificaciones push durante este horario (los emails se enviarán de todos modos)
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Desde
                </label>
                <input
                  type="time"
                  value={preferences.quiet_hours_start || ''}
                  onChange={(e) => setPreferences(prev => ({ ...prev, quiet_hours_start: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hasta
                </label>
                <input
                  type="time"
                  value={preferences.quiet_hours_end || ''}
                  onChange={(e) => setPreferences(prev => ({ ...prev, quiet_hours_end: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Guardando...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Guardar Preferencias</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderPreferences;
